import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import ThirdPartyScripts from "@/components/ThirdPartyScripts";
import SkipLink from "@/components/SkipLink";
import KeyboardHelp from "@/components/KeyboardHelp";
import { A11yProvider } from "@/context/A11yContext";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import AccessibilityPage from "@/pages/Accessibility";
import Trust from "@/pages/Trust";
import NotFound from "@/pages/NotFound";

import MortgagePage from "@/pages/calculators/MortgagePage";
import LoanPage from "@/pages/calculators/LoanPage";
import AutoLoanPage from "@/pages/calculators/AutoLoanPage";
import RefinancePage from "@/pages/calculators/RefinancePage";
import CompoundInterestPage from "@/pages/calculators/CompoundInterestPage";
import RetirementPage from "@/pages/calculators/RetirementPage";
import InvestmentRoiPage from "@/pages/calculators/InvestmentRoiPage";
import DebtPayoffPage from "@/pages/calculators/DebtPayoffPage";
import CreditCardPage from "@/pages/calculators/CreditCardPage";
import SavingsGoalPage from "@/pages/calculators/SavingsGoalPage";
import SimpleInterestPage from "@/pages/calculators/SimpleInterestPage";
import SalaryPage from "@/pages/calculators/SalaryPage";
import TipPage from "@/pages/calculators/TipPage";
import IncomeTaxPage from "@/pages/calculators/IncomeTaxPage";
import RentVsBuyPage from "@/pages/calculators/RentVsBuyPage";
import GeoMortgagePage from "@/pages/calculators/GeoMortgagePage";
import GeoIncomeTaxPage from "@/pages/calculators/GeoIncomeTaxPage";
import GeoTakeHomePage from "@/pages/calculators/GeoTakeHomePage";
import VaLoanPage from "@/pages/calculators/VaLoanPage";
import FhaLoanPage from "@/pages/calculators/FhaLoanPage";
import JumboLoanPage from "@/pages/calculators/JumboLoanPage";
import HelocPage from "@/pages/calculators/HelocPage";
import OvertimeTipsPage from "@/pages/calculators/OvertimeTipsPage";
import StudentLoanPage from "@/pages/calculators/StudentLoanPage";
import RothVsTraditionalPage from "@/pages/calculators/RothVsTraditionalPage";
import EmergencyFundPage from "@/pages/calculators/EmergencyFundPage";
import GeoMetroMortgagePage from "@/pages/calculators/GeoMetroMortgagePage";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (!hash) window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

// Fire a GA4 page_view on every React Router navigation.
// Without this, GA4 only logs the FIRST page a visitor lands on —
// every subsequent in-app navigation would be invisible.
function GA4PageTracker() {
    const { pathname, search } = useLocation();
    useEffect(() => {
        const GA4_ID = process.env.REACT_APP_GA4_ID;
        if (!GA4_ID || typeof window === "undefined" || typeof window.gtag !== "function") return;
        window.gtag("event", "page_view", {
            page_path: pathname + search,
            page_location: window.location.href,
            page_title: document.title,
        });
    }, [pathname, search]);
    return null;
}

function Layout({ children }) {
    return (
        <div className="App flex flex-col min-h-screen">
            <SkipLink />
            <Header />
            <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
                {children}
            </main>
            <Footer />
            <CookieConsent />
            <ThirdPartyScripts />
            <KeyboardHelp />
        </div>
    );
}

function App() {
    return (
        <A11yProvider>
            <BrowserRouter>
                <ScrollToTop />
                <GA4PageTracker />
                <Layout>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/accessibility" element={<AccessibilityPage />} />
                        <Route path="/trust" element={<Trust />} />
                        <Route path="/calculator/mortgage" element={<MortgagePage />} />
                        <Route path="/calculator/loan" element={<LoanPage />} />
                        <Route path="/calculator/auto-loan" element={<AutoLoanPage />} />
                        <Route path="/calculator/refinance" element={<RefinancePage />} />
                        <Route path="/calculator/compound-interest" element={<CompoundInterestPage />} />
                        <Route path="/calculator/retirement" element={<RetirementPage />} />
                        <Route path="/calculator/investment-roi" element={<InvestmentRoiPage />} />
                        <Route path="/calculator/debt-payoff" element={<DebtPayoffPage />} />
                        <Route path="/calculator/credit-card-payoff" element={<CreditCardPage />} />
                        <Route path="/calculator/savings-goal" element={<SavingsGoalPage />} />
                        <Route path="/calculator/simple-interest" element={<SimpleInterestPage />} />
                        <Route path="/calculator/salary" element={<SalaryPage />} />
                        <Route path="/calculator/tip" element={<TipPage />} />
                        <Route path="/calculator/income-tax" element={<IncomeTaxPage />} />
                        <Route path="/calculator/rent-vs-buy" element={<RentVsBuyPage />} />
                        <Route path="/calculator/va-loan" element={<VaLoanPage />} />
                        <Route path="/calculator/fha-loan" element={<FhaLoanPage />} />
                        <Route path="/calculator/jumbo-loan" element={<JumboLoanPage />} />
                        <Route path="/calculator/heloc" element={<HelocPage />} />
                        <Route path="/calculator/overtime-tips" element={<OvertimeTipsPage />} />
                        <Route path="/calculator/student-loan" element={<StudentLoanPage />} />
                        <Route path="/calculator/roth-vs-traditional" element={<RothVsTraditionalPage />} />
                        <Route path="/calculator/emergency-fund" element={<EmergencyFundPage />} />
                        <Route path="/calculator/mortgage/metro/:slug" element={<GeoMetroMortgagePage />} />
                        <Route path="/blog" element={<BlogIndex />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/calculator/mortgage/:state" element={<GeoMortgagePage />} />
                        <Route path="/calculator/income-tax/:state" element={<GeoIncomeTaxPage />} />
                        <Route path="/calculator/take-home/:state" element={<GeoTakeHomePage />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Layout>
            </BrowserRouter>
        </A11yProvider>
    );
}

export default App;
